import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useModal } from '~hooks/useModal';
import { useObject } from '~hooks/useObject';
import { someGiftCard } from '~models/shoppingCart';
import { CURRENT_USER_TARGET } from '~redux/Auth/constants';
import { LOGIN } from '~redux/Modal/constants';
import { actionCreators as homeActions } from '~screens/Dashboard/screens/Home/redux/actions';
import { getRedeemedCodeInformation } from '~screens/Dashboard/screens/Home/redux/utils';
import { redeemedCodeSelector, shoppingCartSelector } from '~screens/Dashboard/screens/Home/redux/selectors';
import { COUNTRIES, CURRENT_COUNTRY_ID } from '~constants/environment';
import { isGiftCardCode } from '~models/giftCard';

import { getInitialState, sendCouponEvent } from './utils';
import { FIELDS } from './constants';

export const useRedeemCode = ({ setFieldValue, onRedeemCoupon }) => {
  const dispatch = useDispatch();
  const { handleOpenModal: openLoginModal } = useModal(LOGIN);
  const redeemedCode = useSelector(redeemedCodeSelector);
  const shoppingCart = useSelector(shoppingCartSelector);
  const currentUser = useSelector(state => !!state.auth[CURRENT_USER_TARGET]);
  const sizesAndCrusts = useSelector(state => state.sizesAndCrusts.data.translations);
  const currentSubsidiary = useSelector(state => state.searchStore.currentSubsidiary);

  const { state, updateValues: updateInputState } = useObject(getInitialState(redeemedCode?.code));
  const { code, activeCode, error } = state;

  const disabledRedeemCode = someGiftCard(shoppingCart?.items);
  const disabledInput = activeCode || disabledRedeemCode;
  const loyaltyEnabled = useSelector(reduxState => reduxState.home.coreSettings?.loyaltyExperienceEnabled);
  const displayLoyaltyText = currentUser && loyaltyEnabled;

  const displaySuccessMessage = !error && redeemedCode?.ok && !redeemedCode?.loading;

  const handleClearCoupon = useCallback(
    (getCartPrices = true) => {
      const { items } = shoppingCart;
      updateInputState({
        code: '',
        activeCode: false,
        error: false
      });
      dispatch(homeActions.clearRedeemCode());
      if (getCartPrices) {
        dispatch(homeActions.getCartPrices(items));
      }

      setFieldValue?.(FIELDS.coupon, '', false);
    },
    [dispatch, setFieldValue, shoppingCart, updateInputState, redeemedCode]
  );

  const firstUpdate = useRef(true);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    handleClearCoupon(false);
  }, [currentSubsidiary.id]);

  useEffect(
    // eslint-disable-next-line complexity
    () => {
      if (redeemedCode?.loading) {
        return;
      }

      if (redeemedCode?.ok && redeemedCode?.code) {
        updateInputState({ activeCode: true, error: null });
        return;
      }

      const shoppingCartRedeemCode =
        shoppingCart.appliedPromos[0]?.appliedCoupon.code || shoppingCart?.giftCard?.code;
      if (!redeemedCode?.code && shoppingCartRedeemCode) {
        updateInputState({ code: shoppingCartRedeemCode });
        dispatch(
          homeActions.redeemCode(
            getRedeemedCodeInformation({
              redeemedCode: shoppingCartRedeemCode,
              amount: shoppingCart?.giftCard?.amount,
              useGiftCard: isGiftCardCode(shoppingCartRedeemCode) && !!shoppingCart?.giftCard
            })
          )
        );
        updateInputState({ activeCode: true });
        return;
      }

      const couponError = !redeemedCode?.ok && redeemedCode?.errorMessage;

      if (couponError) {
        updateInputState({ error: couponError });
        sendCouponEvent(!couponError, code);
        updateInputState({ activeCode: true });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shoppingCart, redeemedCode]
  );

  const redeemCodeRef = useRef(false);
  useEffect(() => {
    if (redeemCodeRef.current && displaySuccessMessage) {
      onRedeemCoupon();
      redeemCodeRef.current = false;
    }
  }, [displaySuccessMessage]);

  const handleRedeemCode = () => {
    redeemCodeRef.current = true;
    const { items } = shoppingCart;
    dispatch(homeActions.applyCoupon({ items, redeemCode: code }));
  };

  const handleChangeInput = e => {
    const { value } = e.target;
    updateInputState({
      activeInput: !value,
      code: value.trim()
    });
  };

  const couponCountriesUnregUsers = [COUNTRIES.cl];
  const enabledCCForUnregUsers = couponCountriesUnregUsers.includes(CURRENT_COUNTRY_ID);

  return {
    redeemedCode,
    inputState: state,
    disabledInput,
    disabledRedeemCode,
    displaySuccessMessage,
    currentUser,
    openLoginModal,
    handleClearCoupon,
    handleRedeemCode,
    handleChangeInput,
    displayLoyaltyText,
    enabledCCForUnregUsers,
    sizesAndCrusts
  };
};
